import { Component } from 'preact'
import Portal from 'preact-portal'
import { ElementChange } from './toolbar'
import { Shared } from './editor'
import classnames from 'classnames'
import Button from '@insided/control/uikit/Button'
import { AnyWidgetConfiguration, WidgetConfiguration } from './WidgetConfiguration'
import { isWidgetShared, ServerGenericPageWidget } from '@insided/customization'

type TypeConfigElement = Pick<ServerGenericPageWidget, 'type' | 'config' | 'title'>

interface Props {
  nested: boolean
  homepageElement: ServerGenericPageWidget
  onChange: (config: TypeConfigElement) => void
  onDone: (config: TypeConfigElement) => void
  onClose: (initialConfig: TypeConfigElement) => void
  widgetTitle: string
  error: string
  isVisible: boolean
  controlUrl: string
}

interface State {
  isVisible: boolean
  isOpen: boolean
  homepageElement: ServerGenericPageWidget
}

const withDerivedTitle = <T extends { title?: string | undefined }>(config: T, titleToOverride?: string) => {
  /**
   * Initially certain components got their title from `el.title` directly, not from `el.config.title`
   * This code uses the second argument as a replacement for the actual title passed to the component
   */
  if (titleToOverride) {
    return {
      ...config,
      title: titleToOverride
    }
  }
  return config
}

export default class WidgetConfigurator extends Component<Props, State> {
  newConfig: TypeConfigElement
  initialConfig: TypeConfigElement
  timeout: number = 0
  changeTimeout: number = 0

  constructor(props: Props) {
    super(props)
    this.newConfig = this.initialConfig = {
      title: props.homepageElement.title,
      type: props.homepageElement.type,
      config: props.homepageElement.config
    }
    this.onClose = this.onClose.bind(this)
    this.queueConfigChange = this.queueConfigChange.bind(this)
    this.state = {
      isVisible: false,
      isOpen: false,
      homepageElement: props.homepageElement
    }
  }

  async done() {
    this.props.onDone(this.newConfig)
  }

  queueConfigChange = (config: Partial<ElementChange['config']> & { title?: string | undefined }) => {
    const mergedConfig = {
      ...this.newConfig,
      /**
       * `title` is on the way to migrate inside `config` object
       * it's backward compatible: `title` is on both places
       */
      title: config.title ?? this.newConfig.title,
      config: {
        ...this.newConfig.config,
        ...config
      }
    }
    this.queueChange(mergedConfig)
  }

  queueChange = (config: ElementChange) => {
    this.newConfig = config

    clearTimeout(this.changeTimeout)
    this.changeTimeout = window.setTimeout(() => {
      this.props.onChange(this.newConfig)
    }, 500)
  }

  shouldComponentUpdate(nextProps: Props, nextState: State) {
    return this.state !== nextState
  }

  onClose() {
    this.props.onClose(this.initialConfig)
  }

  componentWillReceiveProps(newProps: Props) {
    if (newProps.isVisible === this.state.isVisible) return

    clearTimeout(this.timeout)
    if (newProps.isVisible) {
      this.setState({
        isVisible: true,
        homepageElement: newProps.homepageElement
      })
      this.timeout = window.setTimeout(() => {
        this.setState({ isOpen: true })
      }, 1)
      return
    }

    this.setState({ isOpen: false })
    this.timeout = window.setTimeout(() => {
      this.setState({ isVisible: false })
    }, 400)
  }

  render() {
    const { widgetTitle } = this.props
    if (!this.state.isVisible) return null
    return (
      <Portal into='body'>
        <div
          className='widget-configurator'
          style={{
            width: this.state.isOpen ? '400px' : 0
          }}
        >
          <div className='widget-configurator--header'>
            <h4 className='widget-configurator--header-title'>
              {widgetTitle}
              {isWidgetShared(this.state.homepageElement) && <Shared />}
            </h4>
            <Button variant='text' className='close-btn' IconProps={{ size: 24 }} icon='close' onClick={this.onClose} />
          </div>
          <div className={classnames('box__pad', 'widget-configurator--body', 'widget-configurator__controls')}>
            {Boolean(this.props.error) && <div className='widget-configuration_error-box'>{this.props.error}</div>}
            <WidgetConfiguration
              widget={this.state.homepageElement as AnyWidgetConfiguration}
              nested={this.props.nested}
              controlUrl={this.props.controlUrl}
              onChange={this.queueChange}
              onConfigChange={this.queueConfigChange}
            />
          </div>
          <div className='widget-configurator--footer'>
            <Button
              variant='primary'
              size='medium'
              onClick={async () => await this.done()}
              className={DEPRECATED_QA_CLASSNAME}
            >
              Done
            </Button>
          </div>
        </div>
      </Portal>
    )
  }
}

/**
 * @deprecated use data-qa attribute
 */
const DEPRECATED_QA_CLASSNAME = 'btn--insided-brand'
